import React from "react";
import { Button } from "@mui/material";
import { useSnackbar } from "notistack";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { AppointmentOverviewContext } from "../context/appointmentOverviewContext";
import Appointment from "../interface/appointment";
import SearchControllsProps from "../interface/searchControllsProps";
import buildAppointmentsWorkbook from "../functions/buildAppointmentsWorkbook";
import triggerBrowserDownload from "../functions/triggerBrowserDownload";

/**
 * Exports the currently filtered appointments ("you get what you see") to an
 * Excel file. Disabled whenever nothing is filtered — so no appointment types
 * selected or no matches means no empty file is ever produced.
 */
const ExportExcelButton = (): React.JSX.Element => {
  const {
    filtertedAppointments,
    searchControlls,
  }: {
    filtertedAppointments: Appointment[] | null;
    searchControlls: SearchControllsProps;
  } = React.useContext(AppointmentOverviewContext);

  const { enqueueSnackbar } = useSnackbar();
  const [isExporting, setIsExporting] = React.useState<boolean>(false);

  const hasAppointments: boolean =
    !!filtertedAppointments && filtertedAppointments.length > 0;

  const handleExport = async (): Promise<void> => {
    if (!hasAppointments || !filtertedAppointments) return;

    setIsExporting(true);

    try {
      const buffer = await buildAppointmentsWorkbook(filtertedAppointments);
      const { start, end } = searchControlls.date;
      triggerBrowserDownload(buffer, `Terminuebersicht_${start}_${end}.xlsx`);
    } catch (error) {
      console.error("Error exporting appointments to Excel", error);
      enqueueSnackbar("Der Export konnte nicht erstellt werden.", {
        variant: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={!hasAppointments || isExporting}
      variant="outlined"
      color="primary"
      startIcon={<DownloadOutlinedIcon />}
    >
      Export nach Excel
    </Button>
  );
};

export default ExportExcelButton;
